import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Building2, RefreshCw, AlertTriangle,
  CheckCircle, Users, FileText, Zap, Search, Map as MapIcon,
  Navigation, Target, ChevronRight, Play, Settings2, Mail, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAutoAssignPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const { isDarkMode } = useTheme();
  
  const [dashboard, setDashboard] = useState(null);
  const [partners, setPartners] = useState([]);
  const [unassignedCases, setUnassignedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const [notification, setNotification] = useState(null);
  const [showManualAssignModal, setShowManualAssignModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [sendNotification, setSendNotification] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, partnersRes, casesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/assignment/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/assignment/partners`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/assignment/unassigned-cases`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (dashRes.ok) setDashboard(await dashRes.json());
      if (partnersRes.ok) {
        const data = await partnersRes.json();
        setPartners(data.partners || []);
      }
      if (casesRes.ok) {
        const data = await casesRes.json();
        setUnassignedCases(data.cases || []);
      }
    } catch (error) {
      console.error('Load error:', error);
      showNotification('Fehler beim Laden', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleGeocodePartner = async (partnerId) => {
    setActionLoading(partnerId);
    try {
      const res = await fetch(`${API_URL}/api/admin/assignment/partners/${partnerId}/geocode`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        showNotification('Standort erfolgreich ermittelt');
        loadData();
      } else {
        const error = await res.json();
        showNotification(error.detail || 'Geocoding fehlgeschlagen', 'error');
      }
    } catch (error) {
      showNotification('Fehler beim Geocoding', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGeocodeAll = async () => {
    if (!window.confirm('Alle Partner ohne Standort automatisch geocoden?')) return;
    
    setActionLoading('geocode-all');
    try {
      const res = await fetch(`${API_URL}/api/admin/assignment/partners/geocode-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        showNotification(`${result.updated} von ${result.total} Partnern aktualisiert`);
        loadData();
      }
    } catch (error) {
      showNotification('Fehler beim Bulk-Geocoding', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignAll = async () => {
    if (!window.confirm('Alle nicht zugewiesenen Fälle automatisch zuweisen?')) return;
    
    setActionLoading('assign-all');
    try {
      const res = await fetch(`${API_URL}/api/admin/assignment/assign-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        showNotification(`${result.assigned} Fälle zugewiesen`);
        loadData();
      }
    } catch (error) {
      showNotification('Fehler bei der Zuweisung', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignCase = async (caseId) => {
    setActionLoading(caseId);
    try {
      const res = await fetch(`${API_URL}/api/admin/assignment/assign/${caseId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        showNotification(`Fall zugewiesen an ${result.partner_name} (${result.distance_km} km) - E-Mail gesendet!`);
        loadData();
      } else {
        const error = await res.json();
        showNotification(error.detail || 'Zuweisung fehlgeschlagen', 'error');
      }
    } catch (error) {
      showNotification('Fehler bei der Zuweisung', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const openManualAssignModal = (caseItem) => {
    setSelectedCase(caseItem);
    setSendNotification(true);
    setShowManualAssignModal(true);
  };

  const handleManualAssign = async (partnerId) => {
    if (!selectedCase) return;
    
    setActionLoading(`manual-${partnerId}`);
    try {
      const res = await fetch(`${API_URL}/api/admin/assignment/assign-manual/${selectedCase.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          partner_id: partnerId,
          send_notification: sendNotification
        })
      });
      
      if (res.ok) {
        const result = await res.json();
        const notifMsg = result.notification_sent ? ' - E-Mail gesendet!' : '';
        showNotification(`Fall zugewiesen an ${result.partner_name}${notifMsg}`);
        setShowManualAssignModal(false);
        setSelectedCase(null);
        loadData();
      } else {
        const error = await res.json();
        showNotification(error.detail || 'Zuweisung fehlgeschlagen', 'error');
      }
    } catch (error) {
      showNotification('Fehler bei der Zuweisung', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveLocation = async (partnerId) => {
    if (!window.confirm('Standort wirklich entfernen?')) return;
    
    setActionLoading(partnerId);
    try {
      const res = await fetch(`${API_URL}/api/admin/assignment/partners/${partnerId}/location`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        showNotification('Standort entfernt');
        loadData();
      }
    } catch (error) {
      showNotification('Fehler beim Entfernen', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredPartners = partners.filter(p => {
    const matchesSearch = !searchTerm || 
      p.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.company_zip?.includes(searchTerm) ||
      p.company_city?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterLocation === 'all' ||
      (filterLocation === 'with' && p.has_location) ||
      (filterLocation === 'without' && !p.has_location);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <RefreshCw className="animate-spin w-8 h-8 text-brand-green" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} border-b sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Target className="w-6 h-6 text-brand-green" />
                  Automatische Fallzuweisung
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Partner-Standorte verwalten & Fälle zuweisen
                </p>
              </div>
            </div>
            <Button onClick={loadData} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-xs">Partner gesamt</span>
              </div>
              <p className="text-2xl font-bold">{dashboard.partners.total}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-emerald-500 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-xs">Mit Standort</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{dashboard.partners.with_location}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-orange-500 mb-1">
                <FileText className="w-4 h-4" />
                <span className="text-xs">Nicht zugewiesen</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">{dashboard.cases.unassigned}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 shadow-sm`}>
              <div className="flex items-center gap-2 text-blue-500 mb-1">
                <Zap className="w-4 h-4" />
                <span className="text-xs">Geocodierbar</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{dashboard.partners.geocodable}</p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 shadow-sm mb-6`}>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Schnellaktionen
          </h3>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleGeocodeAll}
              disabled={actionLoading === 'geocode-all' || dashboard?.partners.geocodable === 0}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {actionLoading === 'geocode-all' ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <MapPin className="w-4 h-4" />
              )}
              Alle PLZ geocoden ({dashboard?.partners.geocodable || 0})
            </Button>
            <Button
              onClick={handleAssignAll}
              disabled={actionLoading === 'assign-all' || !dashboard?.ready_for_auto_assign}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {actionLoading === 'assign-all' ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Target className="w-4 h-4" />
              )}
              Alle Fälle zuweisen ({dashboard?.cases.unassigned || 0})
            </Button>
          </div>
          {!dashboard?.ready_for_auto_assign && dashboard?.cases.unassigned > 0 && (
            <p className="text-sm text-orange-500 mt-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Zum Zuweisen müssen Partner Standortdaten haben
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {[
              { id: 'partners', label: 'Partner-Standorte', icon: MapPin },
              { id: 'cases', label: 'Nicht zugewiesene Fälle', icon: FileText }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-brand-green border-b-2 border-brand-green'
                      : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.id === 'cases' && unassignedCases.length > 0 && (
                    <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {unassignedCases.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* Partners Tab */}
            {activeTab === 'partners' && (
              <>
                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Partner suchen (Name, PLZ, Stadt)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'
                      }`}
                    />
                  </div>
                  <select
                    value={filterLocation}
                    onChange={(e) => setFilterLocation(e.target.value)}
                    className={`px-4 py-2 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'
                    }`}
                  >
                    <option value="all">Alle Partner</option>
                    <option value="with">Mit Standort</option>
                    <option value="without">Ohne Standort</option>
                  </select>
                </div>

                {/* Partner List */}
                <div className="space-y-3">
                  {filteredPartners.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Keine Partner gefunden</p>
                    </div>
                  ) : (
                    filteredPartners.map(partner => (
                      <div 
                        key={partner.id}
                        className={`${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg p-4`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${
                              partner.has_location 
                                ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                                : 'bg-gray-200 dark:bg-gray-600'
                            }`}>
                              <MapPin className={`w-5 h-5 ${
                                partner.has_location ? 'text-emerald-600' : 'text-gray-500'
                              }`} />
                            </div>
                            <div>
                              <h4 className="font-medium">
                                {partner.company_name || 'Unbenannter Partner'}
                              </h4>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {partner.company_zip && `${partner.company_zip} `}
                                {partner.company_city || partner.email}
                              </p>
                              {partner.has_location && partner.location && (
                                <p className="text-xs text-emerald-600 mt-1">
                                  📍 {partner.location.city} ({partner.location.lat.toFixed(2)}, {partner.location.lng.toFixed(2)})
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {partner.has_location ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleRemoveLocation(partner.id)}
                                disabled={actionLoading === partner.id}
                              >
                                Entfernen
                              </Button>
                            ) : partner.can_geocode ? (
                              <Button
                                size="sm"
                                className="gap-2 bg-blue-600 hover:bg-blue-700"
                                onClick={() => handleGeocodePartner(partner.id)}
                                disabled={actionLoading === partner.id}
                              >
                                {actionLoading === partner.id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <MapPin className="w-4 h-4" />
                                )}
                                Geocoden
                              </Button>
                            ) : (
                              <span className="text-xs text-gray-400">Keine PLZ</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Cases Tab */}
            {activeTab === 'cases' && (
              <div className="space-y-3">
                {unassignedCases.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p>Alle Fälle sind zugewiesen!</p>
                  </div>
                ) : (
                  unassignedCases.map(caseItem => (
                    <div 
                      key={caseItem.id}
                      className={`${isDarkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-100'} border rounded-lg p-4`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">
                            {caseItem.license_plate || 'Kein Kennzeichen'}
                          </h4>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {caseItem.damage_zip && `${caseItem.damage_zip} `}
                            {caseItem.damage_city || caseItem.damage_location || 'Kein Ort'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Eingereicht: {caseItem.submitted_at 
                              ? new Date(caseItem.submitted_at).toLocaleDateString('de-DE')
                              : 'Unbekannt'}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleAssignCase(caseItem.id)}
                          disabled={actionLoading === caseItem.id || dashboard?.partners.with_location === 0}
                          data-testid={`auto-assign-btn-${caseItem.id}`}
                        >
                          {actionLoading === caseItem.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Target className="w-4 h-4" />
                          )}
                          Auto
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2"
                          onClick={() => openManualAssignModal(caseItem)}
                          data-testid={`manual-assign-btn-${caseItem.id}`}
                        >
                          <Users className="w-4 h-4" />
                          Manuell
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Manual Assign Modal */}
      {showManualAssignModal && selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowManualAssignModal(false)} data-testid="manual-assign-modal-overlay">
          <div 
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden`}
            onClick={e => e.stopPropagation()}
            data-testid="manual-assign-modal"
          >
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Partner manuell auswählen</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Fall: {selectedCase.license_plate || 'Ohne Kennzeichen'}
                  </p>
                </div>
                <button 
                  onClick={() => setShowManualAssignModal(false)}
                  className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {/* Notification Toggle */}
              <label className={`flex items-center gap-3 p-3 rounded-lg mb-4 cursor-pointer ${
                isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'
              }`} data-testid="email-notification-toggle">
                <input
                  type="checkbox"
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  data-testid="email-notification-checkbox"
                />
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-emerald-600" />
                  <span>E-Mail-Benachrichtigung an Partner senden</span>
                </div>
              </label>

              {/* Partners with location */}
              <h4 className="font-medium mb-3">Partner mit Standort wählen:</h4>
              <div className="space-y-2">
                {partners.filter(p => p.has_location).length === 0 ? (
                  <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Keine Partner mit Standort verfügbar
                  </p>
                ) : (
                  partners.filter(p => p.has_location).map(partner => (
                    <div 
                      key={partner.id}
                      className={`p-3 rounded-lg border flex items-center justify-between ${
                        isDarkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium">{partner.company_name || 'Partner'}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {partner.location?.city || partner.company_city}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleManualAssign(partner.id)}
                        disabled={actionLoading === `manual-${partner.id}`}
                        data-testid={`assign-to-partner-${partner.id}`}
                      >
                        {actionLoading === `manual-${partner.id}` ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        Zuweisen
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAutoAssignPage;
